import Icon from "./icon.js";

const { redom } = window;
const { el, text } = redom;

const states = {
  PROPOSED: "proposed",
  APPROVED: "approved",
  TAKEOFFREQUESTED: "takeoff requested",
  ACTIVATED: "activated",
  CLOSED: "closed",
};

export default class Feature {
  constructor({ app, api, i18n }) {
    this.i18n = i18n;
    this.app = app;
    this.api = api;
    this.el = el(
      ".info-feature",
      el(
        "table.table",
        el(
          "tr",
          el(
            "td.minwidth",
            (this.legend = el(".legend", (this.legendIcon = new Icon())))
          ),
          el("td", (this.name = el(".name")))
        )
      )
    );
  }

  update(data) {
    const { HIDE_REASON } = window.ENV;
    const { i18n } = this;
    this.data = data;
    const { properties, source } = data;
    const { name, otherReasonInfo, code, state, lower, upper } = properties;

    if (source === "uas") {
      const applicability =
        properties.applicability &&
        typeof properties.applicability === "string" &&
        JSON.parse(properties.applicability);
      const parsedApplicability = parseApplicability(applicability);

      const { justConflict, conflictType, objectType, objectId, reason } =
        properties;

      const message = parseMessage(properties, i18n.LANG);

      if (justConflict) {
        if (
          message.includes(
            "Operation volumes cannot be VLOS when its bounding box size is larger than"
          ) ||
          message.includes("VLOS flight maximum dimensions exceeded")
        ) {
          setHTML(
            this.name,
            i18n("summary.error.VLOS_BOUNDING_BOX_SIZE_TOO_BIG")
          );
        } else {
          if (
            conflictType === "CONFLICTED_OP" &&
            objectType === "OPERATION_PLAN"
          ) {
            let html = i18n(
              "operationplan.conflict.conflictedOp.operationPlan",
              message
            ).replace("{{objectId}}", objectId);

            if (this.api.userType === "pro") {
              html += "<br>";
            }

            setHTML(this.name, html, () => {
              if (this.api.userType === "pro") {
                const suppress = el("input.suppress", {
                  "data-id": objectId,
                  type: "checkbox",
                  style: { width: "auto" },
                });
                const label = el("label", "Suppress");
                const ws = text(" ");
                this.name.appendChild(suppress);
                this.name.appendChild(ws);
                this.name.appendChild(label);
              }
            });
          } else {
            setHTML(this.name, message);
          }
        }
        this.legend.style.backgroundColor = window.COLORS.PROHIBITED;
        this.legendIcon.update("");
      } else {
        const text = [`<b>${name || ""}</b>`];

        if (!HIDE_REASON && reason) {
          text.push(`(${reason})`);

          if (otherReasonInfo) {
            text.push(`<i>${otherReasonInfo}</i>`);
          }
        }

        if (message) {
          text.push(message.trim());
        }

        if (parsedApplicability) {
          text.push("");
          text.push(parsedApplicability);
        }

        text.push(`${lower} &rarr; ${upper}`);

        setHTML(this.name, text.join("<br>"));

        const colors = window.COLORS;

        const { extendedProperties, restriction, _rejecting } = properties;
        this.legend.style.backgroundColor =
          colors[_rejecting ? "PROHIBITED" : restriction] ||
          "hsl(50, 50%, 40%)";

        try {
          const fillColor =
            extendedProperties && JSON.parse(extendedProperties).color.fill;
          this.legend.style.backgroundColor = fillColor;
        } catch (err) {
          //
        }
        this.legendIcon.update("");
      }
    } else if (source === "coordinate") {
      setHTML(this.name, `<b>${i18n("coordinate")}</b><br>${name}</b>`);
      this.legend.style.backgroundColor = "";
      this.legendIcon.update("map-pin-filled");
    } else if (source === "weather-observations") {
      const { text } = properties;
      const split = text.split("\n");
      const results = `<b>${split[0]}</b><br>${split.slice(1).join("<br>")}`;

      setHTML(this.name, results);
      this.legend.style.backgroundColor = "";
      this.legendIcon.update("wind");
    } else if (source === "operationplans") {
      setHTML(
        this.name,
        `<b>${name}</b><br>(${i18n("operationplan")}, ${i18n(
          `operationplan.state.${state}`
        )})<br>`
      );

      this.name._html += "+";

      const button = el(".button");
      button.textContent = i18n("open");
      button.onclick = () => {
        this.app.menubutton.el.click();
        this.api.menuSection = "operationplan";
        this.api.menuId = properties.id;
        this.api.operationPlan = {
          ...JSON.parse(properties.originalPlan),
          _fromGeoJSON: true,
        };
        this.api.update();
      };
      button.style.display = "inline-block";
      button.style.margin = ".5rem 0";
      this.name.appendChild(button);
      this.legend.style.backgroundColor = "";
      this.legendIcon.update("");
    } else {
      this.name.textContent = name || code;
      this.legend.style.backgroundColor = "";
      this.legendIcon.update("");
    }
  }
}

function parseMessage(properties, LANG) {
  try {
    const extendedProperties =
      properties.extendedProperties &&
      typeof properties.extendedProperties === "string"
        ? JSON.parse(properties.extendedProperties)
        : properties.extendedProperties;
    const { localizedMessages = [] } = extendedProperties || {};
    const messages = {};

    for (const localizedMessage of localizedMessages) {
      const { language, message } = localizedMessage;

      messages[language] = message;
      messages[language.split("-")[0]] = message;
    }

    return (
      messages[LANG] ||
      messages[LANG.split("-")[0]] ||
      messages.en ||
      properties.message
    );
  } catch (err) {
    console.error(err);
  }

  return properties.message;
}

function parseApplicability(applicability) {
  const results = [];

  try {
    (applicability || []).forEach((applicability) => {
      const { permanent, startDateTime, endDateTime, schedule } = applicability;

      if (permanent === "YES") {
        if (startDateTime && endDateTime) {
          results.push(
            `${humanDateTime(new Date(startDateTime))}&ndash;${humanDateTime(
              new Date(endDateTime)
            )}: H24`
          );
        } else {
          results.push("H24");
        }
      } else {
        try {
          const parsedSchedule = parseSchedule(applicability);

          if (startDateTime && endDateTime) {
            results.push(
              `${humanDateTime(
                new Date(startDateTime)
              )} &ndash; ${humanDateTime(new Date(endDateTime))}${
                parsedSchedule ? ` : ${parsedSchedule}` : ""
              }`
            );
          } else {
            results.push(parsedSchedule);
          }
        } catch (err) {
          console.error(err);
        }
      }
    });
  } catch (err) {
    console.error(err);
  }

  return results.join("<br>");
}

function parseSchedule(applicability) {
  const { schedule, startDateTime, endDateTime } = applicability;

  return schedule
    .map((item) => {
      const { day, startTime, endTime } = item;

      const startTimeTS = new Date(startDateTime);
      startTimeTS.setUTCHours(Number(startTime.split(":")[0]));
      startTimeTS.setUTCMinutes(
        Number(startTime.split(":")[1].split("Z")[0]),
        0,
        0
      );

      const endTimeTS = new Date(startDateTime);
      endTimeTS.setUTCHours(Number(endTime.split(":")[0]));
      endTimeTS.setUTCMinutes(
        Number(endTime.split(":")[1].split("Z")[0]),
        0,
        0
      );

      return `${day.join(" ")} ${humanTimeRange(startTimeTS, endTimeTS)}`;
    })
    .join(", ");
}

function humanTimeRange(start, end) {
  const { CONVERT_TO_LOCAL_TIMES } = window.ENV;

  if (CONVERT_TO_LOCAL_TIMES) {
    return `${humanTime(start)}&ndash;${humanTime(end)}`;
  } else {
    return `${humanTime(start).slice(-4)}&ndash;${humanTime(end)}`;
  }
}

function humanTime(date) {
  const { CONVERT_TO_LOCAL_TIMES } = window.ENV;

  if (CONVERT_TO_LOCAL_TIMES) {
    const h = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");

    return `${h}:${min}`;
  } else {
    const h = String(date.getUTCHours()).padStart(2, "0");
    const min = String(date.getUTCMinutes()).padStart(2, "0");

    return `${h}:${min} UTC`;
  }
}

function humanDateTime(date) {
  const { CONVERT_TO_LOCAL_TIMES } = window.ENV;

  if (CONVERT_TO_LOCAL_TIMES) {
    const d = date.getDate();
    const m = date.getMonth() + 1;
    const y = date.getFullYear();
    const h = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");

    return `${d}.${m}.${y} ${h}:${min}`;
  } else {
    const d = date.getUTCDate();
    const m = date.getUTCMonth() + 1;
    const y = date.getUTCFullYear();
    const h = String(date.getUTCHours()).padStart(2, "0");
    const min = String(date.getUTCMinutes()).padStart(2, "0");

    return `${d}.${m}.${y} ${h}:${min} UTC`;
  }
}

function setHTML(target, html, cb) {
  if (target._html !== html) {
    target.innerHTML = html;
    target._html = html;

    cb && cb();
  }
}
